import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { JL } from 'jsnlog';
import ScreenSidebar from 'components/sidebar/ScreenSidebar';
import Sidebar from '../sidebar/Sidebar';
import ExperimentSidebar from '../sidebar/ExperimentSidebar';
import Logo from '../../Icons/RMlogo.png';
import RMErrorModal from 'components/main/Error/RMErrorModal';
import { setProductEdition } from 'redux/reducers/experimentReducer';
import { useRMDispatch, useRMSelector } from 'redux/hooks';
import LicenseService from 'business/services/LicenseService';
import RMModal from 'components/modal/RMModal';
import {
  useCookieCounter,
  useGlobalData,
  useSetShowLicenseModal,
  useShowLicenseModal,
} from 'redux/hooks/common';
import { IStyle } from 'Styles';
import AutoUpdate from 'components/dashboard/AutoUpdate';
import StackTrace from 'stacktrace-js';
import { Sidebar as SNFSidebar } from 'components/searchAndFilter';
import { useFetchGlobalData } from 'CustomHooks';
import useCrossTabBusyPublisher from 'CustomHooks/useCrossTabBusyPublisher';
import useCrossTabProductSwitchListener from 'CustomHooks/useCrossTabProductSwitchListener';
import { useLocalStorage } from 'CustomHooks/useLocalStorage';
import FragmentSidebar from 'components/sidebar/FragmentSidebar';
import {
  clearUserDataAndLogout,
  ErrorMessages,
  getCurrentUser,
  GetIngredients,
  showAlertSnackBar,
  validateVersion,
} from 'utils/Global';
import { useIsGlobalDataLoaded } from 'redux/hooks/experiment';
import init from '../../wasm/tuning/image_tuner';
import EAlertType from 'enums/EAlertType';
import ESnackBarPosition from 'enums/ESnackBarPosition';
import { getAppVersion } from '../dashboard/AutoUpdate/AutoUpdate.BL';
import { CacheHelper } from 'utils/CacheHelper';
import { selectedSystemTasksState } from 'redux/selectors/StateSelectors';
import NetworkError from 'utils/NetworkError';
import {
  AUTH_FLOW_PATHS,
  LICENSE_VIOLATION_PATH_FRAGMENT,
  REDIRECT_URL_KEY,
  ROUTE_PATHS,
  LayoutModalType,
  LAYOUT_MODAL_CONFIG,
  DB_SIZE_ALERT_DISMISSED_KEY,
  LICENSE_EXPIRED_KEY,
} from './constants';
import StorageInfoService from 'business/services/StorageInfoService';
import { getFaviconPathForRoute, setAppFavicon } from 'utils/favicon';
import { getHttpMessageHeader } from 'utils/HttpMessageHeader';
import { HTTP_MESSAGE_HEADER } from 'constants/HttpMessageHeaderState';
import { RMLicenseHttpStatusCode } from './Licensing/RMLicenseHttpStatusCode';
import { RMHttpStatusCode } from './RMHttpStatusCode';

const isLicenseHttpStatus = (status: number): boolean =>
  status >= RMLicenseHttpStatusCode.NoLicense &&
  status <= RMLicenseHttpStatusCode.LicenseNotification;

// Extend the Window interface
declare global {
  interface Window {
    JL: typeof JL;
  }
}
//This statement stops JL to automatically log backend errors thrice
window.onerror = function (errorMsg, url, lineNumber, column, errorObj) {
  return false;
};

//This statement stops JL to automatically log backend unhandledexceptions thrice
window.onunhandledrejection = function (event) {
  return false;
};

let lastErrorMessage = '';
let lastErrorTime = 0;

const isDuplicate = (msg) => {
  const now = Date.now();
  if (msg === lastErrorMessage && now - lastErrorTime < 200) {
    return true;
  }
  lastErrorMessage = msg;
  lastErrorTime = now;
  return false;
};

const ErrorMessageKeywords = {
  WebsocketHandshake: 'handshake',
  UnexpectedError: 'Unexpected error',
  FailedToFetch: 'Failed to fetch',
};

const showErrorAlert = (message: ErrorMessages) => {
  showAlertSnackBar(EAlertType.ERROR, ESnackBarPosition.TOP, message);
};

const Layout = (props) => {
  const location = useLocation();
  const path = location.pathname;
  const isAuthFlow = AUTH_FLOW_PATHS.some((p) => path.startsWith(p));
  const bypassLicense = isAuthFlow && !path.startsWith(ROUTE_PATHS.FEATURE_SELECTION);
  const [token, setToken] = useState<string | null>(null);
  const [licenseValidatedToken, setLicenseValidatedToken] = useState<string | null>(null);
  const cookieCounter = useCookieCounter();
  useEffect(() => {
    CacheHelper.get('token').then((value) => setToken(value ?? null));
    const added = CacheHelper.oncacheadded('token', (value) => setToken(value));
    const removed = CacheHelper.oncacheremoved('token', (value) => setToken(value));

    return () => {
      added?.dispose();
      removed?.dispose();
    };
  }, []);
  const licenseComplete = bypassLicense || (!!token && licenseValidatedToken === token);
  useFetchGlobalData(token ?? undefined, !bypassLicense && licenseComplete);
  useCrossTabBusyPublisher();
  useCrossTabProductSwitchListener();
  const systemTasks = useRMSelector(selectedSystemTasksState);
  const hasBatchloadUploadInProgress = systemTasks?.some(
    (task) =>
      task.title?.toLocaleLowerCase().includes('creating batchload experiments') &&
      (task.progress == null || task.progress < 100),
  );

  useEffect(() => {
    if (!hasBatchloadUploadInProgress) {
      return;
    }
    const beforeUnloadListener = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', beforeUnloadListener);
    return () => {
      window.removeEventListener('beforeunload', beforeUnloadListener);
    };
  }, [hasBatchloadUploadInProgress]);

  const [, setLicenseExpired] = useLocalStorage<boolean>(LICENSE_EXPIRED_KEY, false);
  const navigate = useNavigate();
  const isFragmentRoute = path.includes(ROUTE_PATHS.FRAGMENT);
  const expRoute = path.includes(ROUTE_PATHS.EXPERIMENT);
  const screenRoute = path.includes(ROUTE_PATHS.SCREEN);
  const isLoginRoute =
    path.includes(ROUTE_PATHS.LOGIN) ||
    path.includes(ROUTE_PATHS.FORGOT) ||
    path.includes(ROUTE_PATHS.INPUT_OTP);
  const isErrorRoute =
    path.includes(ROUTE_PATHS.ACCESS_CONTROL_ERROR) ||
    path.includes(ROUTE_PATHS.VERSION_MISMATCH);
  const isPFRoute = path.includes(ROUTE_PATHS.PROTEIN);
  const isProject = path.includes(ROUTE_PATHS.PROJECT);
  const isSearchAndFilterRoute = path.includes(ROUTE_PATHS.SEARCH_AND_FILTER);
  const isFeatureSelectionRoute = path.includes(ROUTE_PATHS.FEATURE_SELECTION);
  const isLicenseViolationRoute = path.includes(LICENSE_VIOLATION_PATH_FRAGMENT);

  const currentPathRef = useRef(path);
  currentPathRef.current = path;
  const currentSearchRef = useRef(location.search);
  currentSearchRef.current = location.search;

  const splitPath = path.split('/');
  const routeKey = splitPath[1]?.toLowerCase();
  const globalData = useGlobalData();
  const globalDataLoaded = useIsGlobalDataLoaded();

  const [uncaughtError, setUncaughtError] = useState({
    hasError: false,
    errorMessage: '',
    errorStack: '',
    errorTime: new Date(),
    isLoadingStackTrace: false,
  });
  const dispatch = useRMDispatch();
  const _setProductEdition = useCallback(
    (productEdition: string) => dispatch(setProductEdition(productEdition)),
    [dispatch],
  );
  const showLicensModal = useShowLicenseModal();
  const _setShowLicensModal = useSetShowLicenseModal();
  const [activeModal, setActiveModal] = useState<LayoutModalType | null>(null);
  const [dbSizeAlertData, setDbSizeAlertData] = useState<{
    databaseSizeMB: number;
    maxSizeMB: number;
  } | null>(null);

  const isMountedRef = useRef<boolean>(true);
  const dbSizeAlertCheckedRef = useRef<boolean>(false);

  const checkDbSizeAlert = useCallback(async (): Promise<void> => {
    if (localStorage.getItem(DB_SIZE_ALERT_DISMISSED_KEY) !== null) {
      return;
    }
    try {
      const storageInfoService = new StorageInfoService();
      const alert = await storageInfoService.checkDatabaseSizeLimit();
      if (isMountedRef.current && alert?.isNearLimit) {
        setDbSizeAlertData({
          databaseSizeMB: alert.databaseSizeMB,
          maxSizeMB: alert.maxSizeMB,
        });
        setActiveModal(LayoutModalType.DatabaseSizeAlert);
      }
    } catch (error) {
      console.error('Failed to check database size alert:', error);
    }
  }, []);

  useEffect(() => {
    const loadWasm = async () => {
      try {
        await init();
      } catch (error) {
        console.error('WASM initialization error:', error);
      }
    };

    loadWasm();
  }, []);

  useEffect(() => {
    if (bypassLicense || !token || licenseValidatedToken === token) {
      return;
    }

    let isCurrent = true;
    const licenseService = new LicenseService();
    licenseService
      .getProductEdition()
      .then((res) => {
        if (!isCurrent) {
          return;
        }
        _setProductEdition(res.edition);
        setLicenseValidatedToken(token);
      })
      .catch((reason) => {
        if (!isCurrent) {
          return;
        }
        onUnhandledRejection({ reason } as PromiseRejectionEvent);
        if (
          !isLicenseHttpStatus(reason?.status) &&
          reason?.status !== RMHttpStatusCode.Unauthorized
        ) {
          setLicenseValidatedToken(token);
        }
      });

    return () => {
      isCurrent = false;
    };
  }, [bypassLicense, token, licenseValidatedToken, cookieCounter, _setProductEdition]);

  useEffect(() => {
    if (isAuthFlow || !licenseComplete) {
      dbSizeAlertCheckedRef.current = false;
      return;
    }
    if (!dbSizeAlertCheckedRef.current) {
      dbSizeAlertCheckedRef.current = true;
      checkDbSizeAlert();
    }

    const redirectTo = sessionStorage.getItem(REDIRECT_URL_KEY);

    if (!redirectTo) {
      return;
    }

    const isSafe =
      redirectTo.startsWith(ROUTE_PATHS.ROOT) &&
      !AUTH_FLOW_PATHS.some((p) => redirectTo.startsWith(p));

    if (!isSafe) {
      return;
    }

    const currentPath = location.pathname + location.search;

    if (redirectTo === currentPath) {
      sessionStorage.removeItem(REDIRECT_URL_KEY);
      return;
    }

    navigate(redirectTo, { replace: true });
    sessionStorage.removeItem(REDIRECT_URL_KEY);
  }, [isAuthFlow, licenseComplete, checkDbSizeAlert]);

  useEffect(() => {
    if (showLicensModal) {
      setActiveModal(LayoutModalType.LicenseUpdate);
    }
  }, [showLicensModal]);

  useEffect(() => {
    window.addEventListener('unhandledrejection', onUnhandledRejection);
    window.addEventListener('error', onError);

    return () => {
      window.removeEventListener('unhandledrejection', onUnhandledRejection);
      window.removeEventListener('error', onError);
      isMountedRef.current = false;
    };
  }, []);

  function onUnhandledRejection(err: PromiseRejectionEvent) {
    const { reason } = err;
    console.error('unhandled rejection occured', { err });
    if (isDuplicate(reason?.message)) {
      return;
    }
    if (err.reason instanceof NetworkError) {
      showAlertSnackBar(
        EAlertType.ERROR,
        ESnackBarPosition.TOP,
        'Network error. Please check your connection.',
      );
      return;
    }
    if (err.reason?.Transient) {
      showAlertSnackBar(EAlertType.ERROR, ESnackBarPosition.TOP, reason?.ErrorMessage);
      return;
    }
    /* This is not a proper solution, just a workaround solution for resease, we need to handle these errors in errorboundary */
    switch (reason?.status) {
      case RMHttpStatusCode.Unauthorized:
        clearUserDataAndLogout();
        break;
      case RMLicenseHttpStatusCode.SeatNotRegistered:
        navigate(ROUTE_PATHS.REGISTER_COMPUTER, { state: { detail: reason.status } });
        break;
      case RMLicenseHttpStatusCode.ConcurrencyViolation:
        if (currentPathRef.current !== ROUTE_PATHS.FEATURE_SELECTION) {
          navigate(ROUTE_PATHS.SESSION_TERMINATION, { state: { detail: reason.status } });
        }
        break;
      case RMHttpStatusCode.Forbidden:
      case RMLicenseHttpStatusCode.NoLicense:
      case RMLicenseHttpStatusCode.InActive:
      case RMLicenseHttpStatusCode.ExpiryViolation:
        setLicenseExpired(true);
        navigate(ROUTE_PATHS.LICENSE_VIOLATE, {
          state: {
            detail: err.reason.status,
          },
        });
        break;
      case RMLicenseHttpStatusCode.FeatureViolation:
      case RMLicenseHttpStatusCode.LicenseNotActive:
      case RMLicenseHttpStatusCode.LicenseTerminated:
      case RMLicenseHttpStatusCode.EditionViolation:
      case RMLicenseHttpStatusCode.LicenseUpdating:
      case RMLicenseHttpStatusCode.LicenseNotification:
        const { status, headers } = err.reason;
        navigate(ROUTE_PATHS.LICENSE_VIOLATE, {
          state: {
            detail: status,
            message: getHttpMessageHeader(
              headers,
              HTTP_MESSAGE_HEADER.LicenseViolationMsg,
            ),
            ...(status === RMLicenseHttpStatusCode.LicenseNotification && {
              returnTo: currentPathRef.current + (currentSearchRef.current ?? ''),
            }),
          },
        });
        break;
      case RMLicenseHttpStatusCode.SeatViolation:
        if (currentPathRef.current !== ROUTE_PATHS.FEATURE_SELECTION) {
          navigate(ROUTE_PATHS.SEAT_LICENSE_VIOLATE);
        }
        break;
      case RMLicenseHttpStatusCode.AnonymousError:
      case RMLicenseHttpStatusCode.DBError:
        navigate(ROUTE_PATHS.RM_MESSAGE, { state: { detail: err.reason.status } });
        break;
      case RMHttpStatusCode.FeatureSelection:
        if (currentPathRef.current !== ROUTE_PATHS.FEATURE_SELECTION) {
          navigate(ROUTE_PATHS.HYBRID_SELECTION, {
            state: { detail: err.reason.status },
          });
        }
        break;
      case RMHttpStatusCode.BadRequest:
        showErrorAlert(ErrorMessages.BAD_REQUEST);
        break;
      case RMHttpStatusCode.Timeout:
        showErrorAlert(ErrorMessages.TIMEOUT);
        break;
      default:
        if (reason) {
          const errorString = `${
            reason.ErrorMessage || reason.statusText || reason.message
          }`;
          if (errorString.includes(ErrorMessageKeywords.WebsocketHandshake)) {
            // suppress handshake errors for now(need to replace old implementation), as retry is already in-place
            console.error('WebSocket handshake error:', errorString);
            return;
          }
        }
        if (reason?.StatusCode == RMHttpStatusCode.InternalServerError) {
          const errorString = `${
            reason.ErrorMessage || reason.statusText || reason.message
          }`;
          setUncaughtError({
            hasError: true,
            errorMessage: 'Error: ' + errorString || 'Error',
            errorStack: reason?.StackTrace || reason?.stack,
            errorTime: new Date(),
            isLoadingStackTrace: false,
          });
        } else {
          const errMsg =
            typeof reason === 'string'
              ? ErrorMessageKeywords.UnexpectedError // the detail will be in stack
              : `${reason.ErrorMessage || reason.statusText || reason.message}`;

          const newError = new Error(errMsg);

          newError.stack =
            reason?.StackTrace || reason?.stack
              ? reason?.StackTrace || reason?.stack
              : `${
                  ErrorMessageKeywords.UnexpectedError
                }.\nError: ${errMsg}\nRaw data:\n${getUnexpectedErrorRawData(err)}`;

          throw newError;
        }
    }
  }

  function getUnexpectedErrorRawData(err: PromiseRejectionEvent) {
    if (typeof err.reason === 'string') {
      return err.reason;
    }
    if (typeof err.reason === 'object' && Object.keys(err.reason).length > 0) {
      return JSON.stringify(err.reason);
    }
    return JSON.stringify(err);
  }

  function onError(e: ErrorEvent) {
    const { message, error } = e;

    if (message.includes(ErrorMessageKeywords.WebsocketHandshake)) {
      // suppress handshake errors for now(need to replace old implementation), as retry is already in-place
      console.error('WebSocket handshake error:', message);
      return;
    }
    if (isDuplicate(message)) {
      return;
    }
    if (error.reason?.Transient) {
      showAlertSnackBar(
        EAlertType.ERROR,
        ESnackBarPosition.TOP,
        error.reason?.ErrorMessage,
      );
      return;
    }

    setUncaughtError({
      hasError: true,
      errorMessage: message,
      errorStack: error.stack,
      errorTime: new Date(),
      isLoadingStackTrace: true,
    });

    if (error.stack?.startsWith(ErrorMessageKeywords.UnexpectedError)) {
      setUncaughtError((prvState) => ({
        ...prvState,
        isLoadingStackTrace: false,
        errorStack: error.stack,
      }));
      logJLException(error.stack, message, error);
      return false;
    }

    StackTrace.fromError(error)
      .then((stackErrorList) => {
        console.error({ stackErrorList });
        setUncaughtError((prvState) => ({
          ...prvState,
          isLoadingStackTrace: false,
          errorStack: JSON.stringify(stackErrorList, null, 2),
        }));
        logJLException(stackErrorList, message, error);
      })
      .catch((err) => {
        console.error('Failed to load stack trace', { err });
      })
      .finally(() => {
        // Stop loading if loading still true
        // The stack trace has already been captured earlier, so no need to add it again.
        setUncaughtError((prvState) => ({
          ...prvState,
          isLoadingStackTrace: false,
        }));
      });
    return false;
  }

  const logJLException = (stackErrorList, message, error) => {
    const user = getCurrentUser();
    JL().fatalException(
      {
        msg: 'Uncaught Exception',
        userName: user.name,
        userID: user.id,
        message: message,
        errorStack: JSON.stringify(stackErrorList, null, 2),
      },
      error,
    );
  };

  useEffect(() => {
    setAppFavicon(getFaviconPathForRoute(routeKey));
  }, [routeKey]);

  useEffect(() => {
    if (!isAuthFlow && !licenseComplete) {
      return;
    }

    async function getVersion() {
      let version = sessionStorage.getItem('version');
      if (!validateVersion(version)) {
        version = await getAppVersion();
        if (validateVersion(version)) {
          sessionStorage.setItem('version', version);
        }
      }
      if (validateVersion(version)) {
        document.title = `ROCK MAKER® v${version}`;
      } else {
        document.title = `ROCK MAKER®`;
      }
    }
    if (!expRoute) {
      getVersion();
    }

    // JL().info('starting client app');
  }, [path, isAuthFlow, licenseComplete]);

  const closeErrorModal = () => {
    setUncaughtError({ ...uncaughtError, hasError: false });
  };

  const getModalBody = () => {
    if (activeModal === LayoutModalType.DatabaseSizeAlert && dbSizeAlertData) {
      return `SQL Server Express database size is reaching its maximum limit. Size in MB: ${dbSizeAlertData.databaseSizeMB.toFixed(
        2,
      )}, Maximum allowed in MB: ${dbSizeAlertData.maxSizeMB.toLocaleString()}`;
    }
    return activeModal ? LAYOUT_MODAL_CONFIG[activeModal].body : '';
  };

  const handleModalOk = () => {
    if (activeModal === LayoutModalType.LicenseUpdate) {
      _setShowLicensModal(false);
    }
    if (activeModal === LayoutModalType.DatabaseSizeAlert) {
      localStorage.setItem(DB_SIZE_ALERT_DISMISSED_KEY, '1');
    }
    setActiveModal(null);
  };

  if (!licenseComplete && !isFeatureSelectionRoute) {
    return null;
  }

  if (
    !isAuthFlow &&
    (!GetIngredients() ||
      !globalDataLoaded ||
      globalData.data === null ||
      globalData.isFetching)
  ) {
    return null;
  }

  if (!isLoginRoute && !isPFRoute && !isErrorRoute && !isFeatureSelectionRoute) {
    return (
      <div
        id="layout-wrapper"
        data-testid="layout-wrapper"
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          height: '100%',
          width: '100%',
          display: 'grid',
          gridTemplateAreas: `'top-left center ' 
                                'left center '`,
          gridTemplateColumns: '4.7%  95.3%',
          gridTemplateRows: '9.6% 90.4%',
          overflow: 'hidden',
        }}
      >
        <div
          id="layout-left-container"
          data-testid="layout-left-container"
          style={{
            gridArea: 'left',
            overflow: 'auto',
            scrollbarWidth: 'none',
          }}
        >
          {!expRoute &&
            !screenRoute &&
            !isLoginRoute &&
            !isErrorRoute &&
            !isSearchAndFilterRoute &&
            !isFragmentRoute && <Sidebar isLicenseViolated={isLicenseViolationRoute} />}
          {expRoute &&
            !screenRoute &&
            !isLoginRoute &&
            !isErrorRoute &&
            !isFragmentRoute && (
              <ExperimentSidebar id={`layout-experimentsidebar`} to={splitPath[2]} />
            )}
          {isSearchAndFilterRoute && <SNFSidebar to={splitPath[2]} />}{' '}
          {/*todo: need to fix this,  */}
          {!expRoute &&
            screenRoute &&
            !isLoginRoute &&
            !isErrorRoute &&
            !isFragmentRoute && (
              <ScreenSidebar id="layout-screensidebar" to={splitPath[2]} />
            )}
          {!expRoute &&
            !screenRoute &&
            !isLoginRoute &&
            !isErrorRoute &&
            !isSearchAndFilterRoute &&
            isFragmentRoute && <FragmentSidebar />}
        </div>
        <div
          id="layout-top-left"
          data-testid="layout-top-left"
          style={{
            display: 'flex',
            alignItems: 'center',
            gridArea: 'top-left',
            justifyContent: 'center',
            background: '#1B1B1B',
          }}
          onClick={() => {
            !isLicenseViolationRoute
              ? navigate(ROUTE_PATHS.ROOT)
              : window.location.replace(ROUTE_PATHS.ROOT);
          }}
        >
          <img
            id="layout-logo"
            data-testid="layout-logo"
            src={Logo}
            style={styles.logoImage}
          />
        </div>
        <div
          id="layout-center-container"
          data-testid="layout-center-container"
          style={{
            gridArea: 'center',
            height: '100%',
            position: 'absolute', // Added for focuslevel images height do not stretch from window
            width: '100%',
          }}
        >
          {props.children}
          {isProject && <AutoUpdate showFallback={false} />}
          <RMErrorModal
            hasError={uncaughtError.hasError}
            errorInfo={uncaughtError.errorMessage}
            stackTrace={uncaughtError.errorStack}
            errorTime={uncaughtError.errorTime}
            showClose={true}
            closeErrorModal={closeErrorModal}
            isLoadingStackTrace={uncaughtError.isLoadingStackTrace}
          />
          <RMModal
            id="layout-alertmodal"
            showModal={activeModal !== null}
            setShowModal={handleModalOk}
            onOk={handleModalOk}
            body={getModalBody()}
            heading={activeModal ? LAYOUT_MODAL_CONFIG[activeModal].heading : ''}
            okButtonText="OK"
            isOkOnly={true}
          />
        </div>
      </div>
    );
  }

  return props.children;
};

export default Layout;

const styles: IStyle = {
  logoImage: {
    cursor: 'pointer',
  },
};
